/**
 * The page change an action of the agent may start (a link, a submit), seen
 * from the page it starts on (spec `page-tools.md`). The answer that clicked
 * goes on while this page is still on screen, and its next call, a
 * `page_snapshot` most of the time, would read the page that is leaving: the
 * model takes the click for one that did nothing, and clicks again. The page
 * tools wait here first. On a page that unloads they never answer, and the
 * new page runs the call (`resumeAfterPageNavigation` in the chat store); on
 * a page that stays (a client-side route, a link within the page) they run
 * once it has settled.
 */

/** A page that has not started leaving after this long stays. */
const LEAVE_WAIT_MS = 1_500
/** A navigation that started and never went (a download, a 204) after this long. */
const LEAVE_GIVE_UP_MS = 10_000
/** The quiet that says a route has rendered, and the most it is waited for. */
const QUIET_MS = 400
const QUIET_MAX_MS = 3_000
const POLL_MS = 100

let change: Promise<void> | undefined

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

/** Resolves once the page has gone `QUIET_MS` without a change, or after `QUIET_MAX_MS`. */
export function pageQuiet(): Promise<void> {
    return new Promise((resolve) => {
        const done = () => {
            observer.disconnect()
            clearTimeout(timer)
            clearTimeout(cap)
            resolve()
        }
        let timer = setTimeout(done, QUIET_MS)
        const cap = setTimeout(done, QUIET_MAX_MS)
        const observer = new MutationObserver(() => {
            clearTimeout(timer)
            timer = setTimeout(done, QUIET_MS)
        })
        observer.observe(document.body, {
            childList: true,
            subtree: true,
            characterData: true,
        })
    })
}

/** Called right before the action that may load another document. */
export function startPageChange(): void {
    const from = location.href
    let leaving = false
    const onLeave = () => {
        leaving = true
    }
    window.addEventListener('beforeunload', onLeave)
    window.addEventListener('pagehide', onLeave)
    const current = (async () => {
        const started = Date.now()
        try {
            for (;;) {
                await delay(POLL_MS)
                const elapsed = Date.now() - started
                if (leaving) {
                    if (elapsed > LEAVE_GIVE_UP_MS) {
                        return
                    }
                } else if (location.href !== from) {
                    await pageQuiet()
                    return
                } else if (elapsed > LEAVE_WAIT_MS) {
                    return
                }
            }
        } finally {
            window.removeEventListener('beforeunload', onLeave)
            window.removeEventListener('pagehide', onLeave)
        }
    })()
    change = current
    void current.then(() => {
        if (change === current) {
            change = undefined
        }
    })
}

/** How long a router has to show the new address before the page loads it instead. */
const ROUTE_WAIT_MS = 1_000
/**
 * The elements a route change adds or removes at least: a view replaced,
 * where a clock, a toast or a live counter change a few.
 */
const ROUTE_MIN_ELEMENTS = 20

/** The elements a mutation adds and removes, descendants included. */
const elementsChanged = (record: MutationRecord): number =>
    [...record.addedNodes, ...record.removedNodes].reduce(
        (sum, node) =>
            node instanceof Element
                ? sum + 1 + node.getElementsByTagName('*').length
                : sum,
        0,
    )

/**
 * Asks the site's router for `url`, an address of this site, the way the
 * site's own links do in a single-page app: a history entry and a
 * `popstate`, which Vue Router, React Router and most routers follow without
 * loading the page again. Loaded again, the page would drop the call the
 * answer makes next. Tells whether a router took it: within a moment a view
 * changed (`ROUTE_MIN_ELEMENTS`) or the title did. Nothing loads either way:
 * an address no router takes is the caller's to load.
 */
export async function routeTo(url: string): Promise<boolean> {
    const title = document.title
    let elements = 0
    const observer = new MutationObserver((records) => {
        for (const record of records) {
            elements += elementsChanged(record)
        }
    })
    observer.observe(document.body, { childList: true, subtree: true })
    history.pushState(null, '', url)
    window.dispatchEvent(new PopStateEvent('popstate', { state: null }))
    await delay(ROUTE_WAIT_MS)
    observer.disconnect()
    if (elements >= ROUTE_MIN_ELEMENTS || document.title !== title) {
        await pageQuiet()
        return true
    }
    return false
}

/** What every page tool awaits before it reads or acts. */
export function pageChangeSettled(): Promise<void> {
    return change ?? Promise.resolve()
}

/** The part of the Navigation API the hold uses: not in every DOM typing yet. */
type NavigateEventLike = Event & {
    destination: { url: string; sameDocument: boolean }
    downloadRequest: string | null
    formData: FormData | null
    navigationType: string
}

/** A navigation held back, as the page asked for it. */
type HeldNavigation = {
    url: string
    replace: boolean
    /** A form posted: its entries and the encoding the form asked for. */
    post?: { entries: [string, FormDataEntryValue][]; enctype: string }
}

/** True where navigations can be held: the browser has the Navigation API. */
export const canHoldLeaving = (): boolean =>
    !!(window as unknown as { navigation?: EventTarget }).navigation

/** What an action does with the navigations it holds. */
export type LeavingHold = {
    /** Where the navigation held goes, once one was. */
    readonly destination: string | undefined
    /** Lets navigations go again, and makes the one held, as the page asked for it. */
    release(): void
    /** Lets navigations go again and drops the one held: the call was stopped. */
    drop(): void
}

const methodOf = (form: HTMLFormElement, submitter: Element | null) =>
    (
        submitter?.getAttribute('formmethod') ||
        form.getAttribute('method') ||
        'get'
    ).toLowerCase()

/** Where a submit sends the form: the submitter's own action first. */
const actionOf = (form: HTMLFormElement, submitter: Element | null) => {
    const own = submitter?.getAttribute('formaction')
    return own ? new URL(own, form.baseURI).href : form.action
}

const withoutHash = (url: string) => url.split('#')[0]

/**
 * The holds in force, the latest last. Two actions of one answer hold at
 * once until its resume: a navigation belongs to the latest only, so it is
 * made once, and not by an action the user may have stopped.
 */
const activeHolds: object[] = []

/** Loads a held navigation the way the page asked for it. */
const replay = (held: HeldNavigation) => {
    if (!held.post) {
        if (held.replace) {
            location.replace(held.url)
        } else {
            location.assign(held.url)
        }
        return
    }
    // A form of its own, sent without a `submit` event: the site's handlers
    // already ran on the first one, and must not run twice
    const form = document.createElement('form')
    form.method = 'post'
    form.action = held.url
    form.enctype = held.post.enctype
    form.hidden = true
    const multipart = held.post.enctype === 'multipart/form-data'
    for (const [name, value] of held.post.entries) {
        const input = document.createElement('input')
        input.name = name
        if (typeof value === 'string' || !multipart) {
            // Without multipart a browser sends a file's name only
            input.type = 'hidden'
            input.value = typeof value === 'string' ? value : value.name
        } else {
            input.type = 'file'
            const files = new DataTransfer()
            if (value.size > 0 || value.name) {
                files.items.add(value)
            }
            input.files = files.files
        }
        form.append(input)
    }
    document.body.append(form)
    HTMLFormElement.prototype.submit.call(form)
}

/**
 * Holds every navigation that would load another document in this tab, from
 * an action of the agent until the answer carrying its result has reached
 * the backend (spec `page-tools-improvements.md`, M3): the action runs at
 * once, so its result can say what it did when the site handles it in the
 * page (a single-page app), and the page leaves only once that result is
 * safe. The Navigation API's `navigate` event comes after the site's own
 * handlers, for a form, a link or a script alike, whatever they do to the
 * event's propagation and in shadow roots too: the DOM events missed those.
 *
 * Measured in Chrome (spike of 2026-10-26): a submit made with `click()` is
 * `userInitiated`, so that flag cannot tell the user's own clicks apart; a
 * submitter's `formmethod="post"` leaves the event without `formData`, so
 * the entries of the `submission` the caller names are taken instead.
 * Downloads, same-document routes and navigations that cannot be cancelled
 * go through. Undefined where the browser has no Navigation API.
 */
export function holdLeaving(submission?: {
    form: HTMLFormElement
    submitter: Element | null
}): LeavingHold | undefined {
    const navigation = (window as unknown as { navigation?: EventTarget })
        .navigation
    if (!navigation) {
        return undefined
    }
    let held: HeldNavigation | undefined
    const token = {}
    activeHolds.push(token)
    const listener = (event: Event) => {
        const navigate = event as NavigateEventLike
        if (
            activeHolds[activeHolds.length - 1] !== token ||
            navigate.destination.sameDocument ||
            navigate.downloadRequest !== null ||
            !navigate.cancelable
        ) {
            return
        }
        navigate.preventDefault()
        if (held) {
            return
        }
        const { form, submitter } = submission ?? {}
        const posted =
            navigate.formData ??
            (form &&
            methodOf(form, submitter ?? null) === 'post' &&
            // The form's own submit only: an address its handler loads with
            // a script (`location.assign`) is a plain GET
            withoutHash(navigate.destination.url) ===
                withoutHash(actionOf(form, submitter ?? null))
                ? new FormData(
                      form,
                      submitter instanceof HTMLElement ? submitter : null,
                  )
                : null)
        held = {
            url: navigate.destination.url,
            replace: navigate.navigationType === 'replace',
            post: posted
                ? {
                      entries: [...posted.entries()],
                      enctype:
                          submitter?.getAttribute('formenctype') ||
                          form?.enctype ||
                          'application/x-www-form-urlencoded',
                  }
                : undefined,
        }
    }
    navigation.addEventListener('navigate', listener)
    const stop = () => {
        navigation.removeEventListener('navigate', listener)
        const index = activeHolds.indexOf(token)
        if (index >= 0) {
            activeHolds.splice(index, 1)
        }
    }
    return {
        get destination() {
            return held?.url
        },
        release() {
            stop()
            if (held) {
                replay(held)
            }
        },
        drop: stop,
    }
}
